import { useMemo, useState } from 'react';
import { allVerificationCases, VERIFICATION_COVERAGE } from '../data/verificationCases';
import { runCases, summarise } from '../calculator/verification';
import { formatNumber } from '../utils/format';
import { APP_META } from '../data/provenance';

export function VerificationCenter() {
  const [showAll, setShowAll] = useState(false);

  const { results, summary } = useMemo(() => {
    const cases = allVerificationCases();
    const results = runCases(cases);
    return { results, summary: summarise(results) };
  }, []);

  const failures = results.filter((r) => !r.passed);
  const visible = showAll ? results : results.slice(0, 6);

  return (
    <div className="verification">
      <div className="verification-status">
        <div className="status-block status-pending">
          <p className="status-label">Independent verification</p>
          <p className="status-value">{summary.headline}</p>
          <p className="status-note">
            {summary.hasIndependentData
              ? 'Expected values were produced outside this codebase by a separate party.'
              : 'No externally produced reference dataset has been supplied. Until one is, this calculator must not be described as verified or validated.'}
          </p>
        </div>
        <div className="status-block status-dev">
          <p className="status-label">Development tests</p>
          <p className="status-value">
            {summary.developmentPassed} / {summary.developmentTotal} passed
          </p>
          <p className="status-note">
            Expected values were authored alongside the implementation, using a separate Python script but the same
            author and the same assumptions. That demonstrates internal consistency, not independence.
          </p>
        </div>
      </div>

      <p className="verification-lede">
        These cases run live in your browser against the same functions that produced your result above — not against a
        stored screenshot of a previous test run.
      </p>

      {failures.length > 0 && (
        <p className="verification-failures">
          {failures.length} case{failures.length === 1 ? '' : 's'} did not match the expected value:{' '}
          {failures.map((f) => f.testCase.caseId).join(', ')}
        </p>
      )}

      <div className="table-wrap">
        <table className="data-table verification-table">
          <thead>
            <tr>
              <th scope="col">Case</th>
              <th scope="col">Scenario</th>
              <th scope="col">Expected AAF</th>
              <th scope="col">Actual AAF</th>
              <th scope="col">Expected AAT</th>
              <th scope="col">Actual AAT</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.testCase.caseId}>
                <th scope="row" className="num">
                  {r.testCase.caseId}
                </th>
                <td className="case-desc">{r.testCase.description}</td>
                <td className="num">{formatNumber(r.testCase.expectedAAF, 6)}</td>
                <td className="num">{formatNumber(r.actualAAF, 6)}</td>
                <td className="num">{formatNumber(r.testCase.expectedAATDays, 6)}</td>
                <td className="num">{formatNumber(r.actualAATDays, 6)}</td>
                <td>
                  <span className={`pill ${r.passed ? 'pill-pass' : 'pill-fail'}`}>{r.passed ? 'match' : 'mismatch'}</span>
                  <span className="pill pill-tier">
                    {r.testCase.status === 'independent' ? 'independent' : 'development'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {results.length > 6 && (
        <button type="button" className="secondary-button" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer cases' : `Show all ${results.length} cases`}
        </button>
      )}

      <div className="coverage">
        <h3 className="panel-title">What the cases cover</h3>
        <ul className="coverage-list">
          {VERIFICATION_COVERAGE.map((c) => (
            <li key={c.category}>
              <span className="coverage-category">{c.category}</span>
              <span className="coverage-cases">{c.covered}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="verification-meaning">
        <h3 className="panel-title">Two different things, kept apart</h3>
        <dl>
          <div>
            <dt>Software unit testing</dt>
            <dd>
              Checks that the code does what its author intended — conversions, rounding, error handling, sensitivity
              arithmetic. Run with <code>npm test</code>. Present and passing.
            </dd>
          </div>
          <div>
            <dt>Independent calculator verification</dt>
            <dd>
              Checks that the intended behaviour is itself correct, against expected values produced by someone else,
              some other way. Not present. Pending.
            </dd>
          </div>
        </dl>
        <p className="field-help">
          Calculator verification version {APP_META.calculatorVerificationVersion} · dataset{' '}
          {APP_META.verificationDatasetVersion}
        </p>
      </div>
    </div>
  );
}
